<template lang="pug">
Transition(name="ease-in-out")
  div(
    :class="$style.activePrelisting"
    v-if="listings.length && showActivePrelistings")
    .container(v-if="listings.length > 1")
      div(:class="$style.mainBar")
        h5(class="pt-2") {{ $t('quote_banner.quote_count').replace('{count}', listings.length) }}
        a(
          :class="[$style.listingsToggle, multipleShow ? $style.toggleActive : '']"
          @click="multipleShow = !multipleShow")
          Icon(
            name="i:angle-down"
            :class="$style.toggleIcon"
            :size="26")
        div(:class="$style.close")
          a(@click="bannerClose()")
            Icon(
              name="i:close-1"
              :size="16")

      Transition(name="ease-in-out")
        div(
          :class="$style.prelistings"
          v-if="multipleShow")
          div(
            :class="$style.singlePrelisting"
            v-for="listing in listings"
            :key="listing.pre_listing_id")
            Icon(
              :class="$style.categoryIcon"
              :name="getIcon(listing)"
              :size="28")
            h5(:class="$style.infoTitle") {{ getTitle(listing) }}
            p
              span(v-if="getItems(listing)") {{ getItems(listing) }}
            div(:class="$style.price")
              h5(v-if="listing.lowest_price") {{ $t('quote_banner.price_prefix') + listing.lowest_price + $t('quote_banner.price_suffix')}}
            a(
              :class="[$style.btn, listing.lowest_price ? '' : $style.withoutPrice]"
              :href="$config.public.v4Host + listing.redirect_path") {{$t('quote_banner.continue')}}
    .container(
      v-else
      :class="$style.singlePrelistingWrap")
      div(:class="$style.singlePrelisting")
        Icon.text-gray-800(
          :class="$style.categoryIcon"
          :name="getIcon(listings[0])"
          :size="28")
        h5(:class="$style.infoTitle")
          span.hidden(class="md:block") {{ $t('quote_banner.title') }}
          span(class="md:hidden") {{ getTitle(listings[0]) }}
        p
          span.hidden(class="md:block") {{ getTitle(listings[0]) }}
          span(
            class="md:hidden"
            v-if="getItems(listings[0])") {{ getItems(listings[0]) }}
        div(:class="$style.price")
          h5(v-if="listings[0].lowest_price") {{ $t('quote_banner.price_prefix') + listings[0].lowest_price + $t('quote_banner.price_suffix') }}
        a(
          :class="[$style.btn, listings[0].lowest_price ? '' : $style.withoutPrice]"
          :href="$config.public.v4Host + listings[0].redirect_path") {{$t('quote_banner.continue')}}
        div(:class="$style.close")
          a(@click="bannerClose()")
            Icon(
              name="i:close-1"
              :size="16")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { getCategoryByV4Id } from "~/utils/categories"
import { getv4LocaleFromI18nLocale } from "~/scripts/useful-functions"

interface ComponentData {
  multipleShow: boolean
}

export default defineComponent({
  name: "ActivePrelisting",

  emits: ['banner-close'],

  props: {
    listings: {
      type: Array as () => Record<string, any>[],
      required: true
    },
    showActivePrelistings: {
      type: Boolean,
      default: true
    }
  },

  data(): ComponentData {
    return {
      multipleShow: false
    }
  },



  methods: {
    getCategoryByV4Id,

    bannerClose(): void {
      const resDate = new Date()
      resDate.setDate(resDate.getDate() + 14)
      useCookie("active_prelistings_banner", {
        expires: resDate,
        path: "/",
        secure: true
      }).value = "true"

      this.$emit('banner-close')
    },

    getTitle(listing: Record<string, any>): string {
      const category = this.getCategoryByV4Id(listing.category_id)
      let categoryName = category?.name
      if (listing.start_property_type === "office" || listing.end_property_type === "office") {
        categoryName = "Office Removals"
      }
      let title = category
        ? this.$t("category." + categoryName) + " " + this.$t("quote_banner.delivery_lower")
        : this.$t("quote_banner.delivery_upper")

      if (listing.pickup && listing.pickup.town) {
        title += this.$t("quote_banner.from") + listing.pickup.town
      }
      if (listing.delivery && listing.delivery.town) {
        title += this.$t("quote_banner.to") + listing.delivery.town
      }

      return title as string
    },

    getIcon(listing: Record<string, any>): string {
      const category = this.getCategoryByV4Id(listing.category_id)
      return "i:" + category?.icon
    },

    getItems(listing: Record<string, any>): string {
      if (listing.items.length > 0) {
        let items = ""
        listing.items.every((itemValue, itemIndex) => {
          if (items.length > 50) {
            items += "..."
            return false
          }
          if (itemIndex != 0) {
            items += ", "
          }
          items += itemValue.qty + "x" + " " + itemValue.name

          return true
        })

        return items
      }

      return ""
    }
  }
})
</script>

<style lang="scss" module>
@mixin gridItems($length) {
  display: grid;

  @for $i from 1 to $length + 1 {
    > *:nth-child(#{$i}) {
      grid-area: _#{$i};
    }
  }
}

body .activePrelisting {
  @apply py-ds-md text-gray-800;
  background: #8dc9eb;

  .mainBar {
    @apply flex w-full items-start;

    .close {
      @apply ml-auto;
      padding-top: 1px;
    }

    p {
      @apply pt-ds-xxxs;
    }
  }

  .listingsToggle {
    @apply ml-ds-md;

    .toggleIcon {
      -webkit-transition: all linear 0.2s;
      transition: all linear 0.2s;
    }

    &.toggleActive {
      .toggleIcon {
        transform: rotate(-180deg);
      }
    }
  }

  path {
    @apply text-gray-800;
  }

  p,
  p span {
    @apply ms-sm;
  }

  h5,
  h5 span {
    @apply font-bold ms-base;
  }

  .prelistings {
    .singlePrelisting {
      @apply mt-ds-md pt-ds-md;
      grid-template-columns: min-content auto min-content;
      @include gridItems(5);

      grid-template-areas:
        "_1 _2 _4"
        "_1 _3 _4"
        "_5 _5 _5";

      & + .singlePrelisting {
        border-top: 1px solid rgba(0, 0, 0, 0.3);
      }
    }
  }

  .categoryIcon {
    @apply mr-ds-md;
  }

  .price {
    @apply text-right;

    h5 {
      @apply whitespace-nowrap pl-ds-md;
    }
  }

  .btn {
    @apply rounded-md bg-white text-gray-800 py-ds-sm px-ds-md leading-xl2 cursor-pointer w-full mt-ds-md block font-bold text-center;
    font-size: 12.446px;

    &:hover {
      @apply no-underline;
      filter: brightness(85%);
    }
  }

  h5.infoTitle {
    @apply pb-ds-xxs;
  }

  .singlePrelisting {
    @apply items-center;
  }

  .singlePrelistingWrap {
    .close {
      @apply self-start pl-ds-md;
    }

    .categoryIcon {
      @apply mb-ds-xxs;
    }

    .singlePrelisting {
      grid-template-columns: min-content auto auto min-content;
      @include gridItems(6);

      grid-template-areas:
        "_1 _2 _2 _6"
        "_3 _3 _4 _6"
        "_5 _5 _5 _5";
    }
  }
}

@screen md {
  body .activePrelisting {
    .listingsToggle {
      @apply ml-ds-lg;
    }

    .btn {
      @apply w-auto mt-0 inline-block ml-ds-xxxl;
      font-size: 12.64px;
      line-height: 23px;
    }

    .mainBar h5 {
      @apply mt-ds-xxxs;
    }

    .prelistings {
      .singlePrelisting {
        grid-template-columns: min-content auto min-content min-content;
        grid-template-areas:
          "_1 _2 _4 _5"
          "_1 _3 _4 _5";
      }
    }

    .singlePrelistingWrap {
      h5.infoTitle {
        @apply ml-0;
      }

      .close {
        @apply ml-ds-xxl pl-0 self-center;
      }

      .singlePrelisting {
        grid-template-columns: min-content auto min-content min-content min-content;
        grid-template-areas:
          "_1 _2 _4 _5 _6"
          "_1 _3 _4 _5 _6";
      }

      .categoryIcon {
        @apply mb-0;
      }
    }
  }
}
</style>
